import { useState, useEffect } from 'react';
import { doc, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

// Reads Instagram data synced by the syncZernioInstagramData Cloud Function (Zernio's API),
// not a direct Meta Graph API call — same pattern as useLinkedInZernio.js.
export const useInstagram = () => {
  const [meta, setMeta] = useState(null);
  const [followerStats, setFollowerStats] = useState([]);
  const [postAnalytics, setPostAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubMeta = onSnapshot(
      doc(db, 'socialMetrics', 'instagram'),
      (snap) => { setMeta(snap.exists() ? snap.data() : null); setLoading(false); },
      (err) => { console.error('Instagram (Zernio) meta listener:', err); setLoading(false); }
    );
    const unsubStats = onSnapshot(
      query(collection(db, 'socialMetrics', 'instagram', 'followerStats'), orderBy('date', 'desc'), limit(7)),
      (snap) => setFollowerStats(snap.docs.map((d) => d.data())),
      (err) => console.error('Instagram (Zernio) followerStats listener:', err)
    );
    const unsubPosts = onSnapshot(
      query(collection(db, 'socialMetrics', 'instagram', 'postAnalytics'), orderBy('publishedAt', 'desc'), limit(12)),
      (snap) => setPostAnalytics(snap.docs.map((d) => d.data())),
      (err) => console.error('Instagram (Zernio) postAnalytics listener:', err)
    );
    return () => { unsubMeta(); unsubStats(); unsubPosts(); };
  }, []);

  const current = followerStats[0]?.followers ?? null;

  // Reshaped to the exact fields src/App.js already reads (followers, postCount,
  // engagementRate, recentPosts, lastFetched) so App.js needs no changes this pass.
  // NOTE — two real differences from the old Meta-sourced values, not just a rename:
  //   - postCount here is "posts Zernio has synced analytics for in its lookback window",
  //     not the lifetime media_count the old Graph API profile call returned.
  //   - thumbnail_url/media_url are mapped defensively in case Zernio's post-analytics
  //     payload includes them, but Zernio's documented response doesn't list a media/cover
  //     URL field. If it's absent, `cover` in BusinessView's post grid resolves to null and
  //     falls back to the existing 🎬 placeholder icon — it degrades, it doesn't break.
  const recentPosts = postAnalytics.map((p) => ({
    views: p.impressions ?? 0,
    like_count: p.likes ?? 0,
    comments_count: p.comments ?? 0,
    thumbnail_url: p.thumbnailUrl || null,
    media_url: p.mediaUrl || null,
    permalink: p.platformPostUrl || null,
    caption: p.content || '',
    timestamp: p.publishedAt || null,
  }));
  const totalEngagement = postAnalytics.reduce((sum, p) => sum + (p.likes || 0) + (p.comments || 0), 0);
  const engagementRate = postAnalytics.length > 0 && current
    ? parseFloat(((totalEngagement / postAnalytics.length) / current * 100).toFixed(2))
    : 0;

  const data = meta ? {
    followers: current,
    postCount: postAnalytics.length,
    engagementRate,
    recentPosts,
    lastFetched: meta.lastRunAt || null,
  } : null;

  return { data, loading };
};
